import * as React from 'react'
import {
  Box,
  Grid,
  Icon,
  IconButton,
  Tooltip,
  Fab,
  styled,
} from '@design/pt-ui'

import { SupervisorAccount as SupervisorAccountIcon } from '@mui/icons-material'

import Filter from './Filter'
import GridTable from './GridTable'
import Actions from './Actions'
import Centered from '../../components/Centered'
import ProjectDialog from '../../components/ProjectDialog'
import Loader from '../../components/Loader'

import { PageProps as ProjectsDashboardPageProps } from '../../interfaces/pages/projects'
import { unsavedProject, Project } from '../../interfaces/entities/project'
import { ProjectsFilter } from '../../interfaces/services/projects'
import { Link } from 'react-router-dom'
import { Descriptive } from '../../interfaces/base'
import { translateWithUserLocale } from '../../utils/i18n'
import DI from '../../app/di'

import { getAppEnv, getConfigParam } from '../../utils/config'
import OutdatedFormatsetsMessageDialog from '../ProjectView/OutdatedFormatsetsMessageDialog'

export const projectColumns = [
  { name: 'client_name', title: translateWithUserLocale('client') },
  { name: 'name', title: translateWithUserLocale('project') },
  {
    name: 'memoria_campaigns',
    title: translateWithUserLocale('campaigns'),
  },
  {
    name: 'deadline',
    title: translateWithUserLocale('projects.deadline_delivery'),
  },
  { name: 'state', title: translateWithUserLocale('status') },
  { name: 'action', title: translateWithUserLocale('action') },
]

const ProjectLink = styled(Link)(({ theme }) => ({
  textDecoration: 'none',
  color: theme.palette.custom.link.main,
  '&:hover': {
    color: theme.palette.custom.link.hover,
  },
}))

const ActionFab = styled(Fab)(({ theme }) => ({
  margin: theme.spacing(1),
  borderRadius: '50%',
  width: '50px',
  height: '50px',
}))

export type IProps = ProjectsDashboardPageProps

function Layout(props: IProps) {
  const [showFavorites, setShowFavorites] = React.useState(false)
  const [invalidCopyProject, setInvalidCopyProject] = React.useState({
    open: false,
    invalidFormatsetsText: '',
    newProjectId: '',
  })

  const handleAction = (
    action: Descriptive,
    project: Project,
    option?: string,
  ) => {
    if (action.name === 'reopen') {
      props.doProjectAction(action, project, {
        reopenCreatives: option === 'true',
      })
    } else {
      props.doProjectAction(action, project)
    }
  }

  const loading = props.page.loading

  const openInvalidCopyProjectDialog = (
    invalidFormatsetsText: string,
    copiedProjectId: string,
  ) => {
    setInvalidCopyProject({
      open: true,
      invalidFormatsetsText,
      newProjectId: copiedProjectId,
    })
  }

  const closeInvalidCopyProjectDialog = () => {
    setInvalidCopyProject({
      open: false,
      invalidFormatsetsText: '',
      newProjectId: '',
    })
  }

  const projectRows = props.page.result.projects.map((project) => {
    return {
      client_name: project.client.name,
      name: (
        <ProjectLink to={`/project/${project.id}`}>{project.name}</ProjectLink>
      ),
      memoria_campaigns: project.campaigns
        .map((campaign) => campaign.name)
        .join(', '),
      deadline: project.deadline.format('DD.MM.YYYY'),
      state: project.status.title,
      action: (
        <Actions
          project={project}
          onActionClick={handleAction}
          onProjectSave={(p: any) => {
            props.onProjectSave(p, { created: false })
          }}
          onFavoriteChange={() => handleFavoriteChange()}
          canCopy={!props.user.groups.includes('client')}
          openInvalidCopyProjectDialog={openInvalidCopyProjectDialog}
        />
      ),
    }
  })

  const handleFavoriteChange = () => {
    props.getProjects({
      ...props.page.filter,
      favorite: showFavorites,
      pageNo: props.page.result.pagination.page,
    })
  }

  const handleSetShowFavorites = () => {
    setShowFavorites((prev) => {
      let params = {
        ...props.page.filter,
        favorite: !prev,
        pageNo: prev === false ? 1 : null,
      }
      props.getProjects(params)
      props.onFilterChange({ ...props.page.filter, favorite: !prev })
      return !prev
    })
  }

  const handleGoToCopiedProject = () => {
    const history = DI.get('history')
    history.push(`/project/${invalidCopyProject.newProjectId}`)
  }

  const handleDialogOnConfirm = () => {
    const history = DI.get('history')
    history.push(`/`)
  }

  const redirect = () => {
    const history = DI.get('history')
    history.push('/user-management')
  }
  return (
    <>
      <Box
        sx={{
          marginTop: 3,
          display: 'flex',
          alignItems: 'center',
          flexFlow: 'column',
        }}>
        <Grid spacing={3} container={true} sx={{ width: '100%' }}>
          <Grid
            size={{
              xs: 12,
              md: 12,
            }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                marginLeft: '15px',
                marginRight: '35px',
              }}>
              <h2>{translateWithUserLocale('projects_overview')}</h2>

              {props.user.restrictedAccess ? (
                ''
              ) : (
                <IconButton onClick={redirect}>
                  <SupervisorAccountIcon />
                </IconButton>
              )}
            </Box>
          </Grid>
          <Grid size={11}>
            <Filter
              filter={props.page.filter}
              userGroups={props.user.groups}
              onFilterChange={(filter: ProjectsFilter) => {
                props.onFilterChange(filter)
              }}
            />
          </Grid>
          <Grid
            sx={{
              flexDirection: 'column',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
            size={1}>
            {props.user.groups.includes('client') ? null : (
              <ProjectDialog
                onSave={async (project: any) => {
                  props.onProjectSave(project, { created: true })
                }}
                activator={
                  <Centered>
                    <ActionFab
                      variant="extended"
                      color="primary"
                      aria-label="Add">
                      <Icon>add</Icon>
                    </ActionFab>
                  </Centered>
                }
                project={unsavedProject()}
              />
            )}

            <Centered>
              <Tooltip
                title={
                  showFavorites
                    ? ''
                    : translateWithUserLocale('projects.show_favorite_projects')
                }>
                <ActionFab
                  onClick={handleSetShowFavorites}
                  variant="extended"
                  color={showFavorites ? 'primary' : 'default'}
                  aria-label="Show Favorites">
                  <Icon fontSize="large">star</Icon>
                </ActionFab>
              </Tooltip>
            </Centered>
          </Grid>

          <Grid size={12}>
            {loading ? (
              <Loader
                variant="overlay"
                message={translateWithUserLocale('loaders.loading_projects')}
              />
            ) : null}
            <GridTable
              columns={projectColumns}
              rows={projectRows}
              page={props.page.result.pagination.page}
              pageSize={props.page.tablePagination.resultsPerPage}
              ordering={props.page.tablePagination.ordering}
              onCurrentPageChange={props.onCurrentPageChange}
              onPageSizeChange={props.onPageSizeChange}
              onSortingChange={props.onSortingChange}
              totalRows={props.page.result.pagination.totalRows}
            />
          </Grid>
        </Grid>
      </Box>
      {getAppEnv().appEnv === 'review' ? (
        <h2 id="branch_name_indicator_visible">
          {getConfigParam('REACT_APP_BRANCH_NAME', '')}
          {' - '}
          {getConfigParam('REACT_APP_COMMIT_SHORT_SHA', '')}
        </h2>
      ) : null}
      <OutdatedFormatsetsMessageDialog
        open={invalidCopyProject.open}
        onClose={closeInvalidCopyProjectDialog}
        invalidFormatsets={invalidCopyProject.invalidFormatsetsText}
        type="copy"
        goToCopiedProject={handleGoToCopiedProject}
        onConfirm={handleDialogOnConfirm}
      />
    </>
  )
}

export default Layout
